/*
 * @format
 */

// What the app and the web app both need to know about the Halo API's
// passkeys: its limit, its error codes and how to describe a passkey. The
// wording of messages stays with each app, as they differ ("this phone" in
// the app, "this browser" on the web). Kept
// apart from api/passkeys so it has no dependencies and isn't mocked away
// with the API client in tests.

/**
 * The most passkeys an account can have (MAX_PASSKEYS_PER_ACCOUNT in
 * darwin_api); the API refuses more.
 */
export const MAX_PASSKEYS = 10;

/** The longest name the API takes for a passkey, in UTF-16 code units. */
export const MAX_PASSKEY_NAME_LENGTH = 64;

// Unicode's control characters (Cc), which the API strips from a name.
const CONTROL_CHARACTERS = /[\u0000-\u001F\u007F-\u009F]/g;

/**
 * A name for a new passkey the API will take, from the device's: cleaned as the
 * API cleans it (control characters stripped, trimmed) so it can't come out
 * empty there, and cut to MAX_PASSKEY_NAME_LENGTH, as the API counts it, without
 * splitting an emoji or other character made of two code units. `fallback` when
 * nothing is left. The API refusing the name would refuse the passkey with it,
 * on every try.
 */
export function passkeyName(name, fallback) {
  let cut = (name || '')
    .replace(CONTROL_CHARACTERS, '')
    .trim()
    .slice(0, MAX_PASSKEY_NAME_LENGTH);
  const last = cut.charCodeAt(cut.length - 1);
  if (last >= 0xd800 && last <= 0xdbff) {
    // The first half of a pair the cut came between.
    cut = cut.slice(0, -1);
  }
  return cut.trim() || fallback;
}

/** Error codes the passkey endpoints answer with. */
export const PASSKEY_ERRORS = {
  ALREADY_REGISTERED: 'PASSKEY_ALREADY_REGISTERED',
  CHALLENGE_EXPIRED: 'REGISTRATION_CHALLENGE_EXPIRED',
  FORBIDDEN: 'FORBIDDEN',
  INVALID_REGISTRATION: 'INVALID_PASSKEY_REGISTRATION',
  LIMIT_REACHED: 'PASSKEY_LIMIT_REACHED',
  NOT_FOUND: 'PASSKEY_NOT_FOUND',
  RECENT_SIGN_IN_REQUIRED: 'PASSKEY_RECENT_SIGN_IN_REQUIRED',
  SIGNED_IN_BEFORE_RESET: 'PASSKEY_SIGNED_IN_BEFORE_RESET',
  UNAVAILABLE: 'PASSKEYS_UNAVAILABLE',
  VERIFICATION_FAILED: 'PASSKEY_VERIFICATION_FAILED',
};

/**
 * What the API's refusal (`err`) means, for each app to put in its own words:
 *
 * - limitReached: the account has MAX_PASSKEYS already
 * - recentSignInRequired: adding a passkey needs a sign-in from the last 10
 *   minutes
 * - signedInBeforeReset: the password was reset since this session signed in
 * - sessionEnded: the session has ended (401)
 *
 * null for anything else.
 */
export function passkeyApiProblem(err) {
  switch (err?.code) {
    case PASSKEY_ERRORS.LIMIT_REACHED:
      return 'limitReached';
    case PASSKEY_ERRORS.RECENT_SIGN_IN_REQUIRED:
      return 'recentSignInRequired';
    case PASSKEY_ERRORS.SIGNED_IN_BEFORE_RESET:
      return 'signedInBeforeReset';
    default:
      return err?.status === 401 ? 'sessionEnded' : null;
  }
}

// The problems only signing in again can fix.
const SIGN_IN_PROBLEMS = [
  'recentSignInRequired',
  'signedInBeforeReset',
  'sessionEnded',
];

/**
 * Whether the API needs the user to sign in again before it can help (see
 * passkeyApiProblem). Trying again can't help, so the apps offer to sign out
 * instead.
 */
export const needsSignIn = err =>
  SIGN_IN_PROBLEMS.includes(passkeyApiProblem(err));

// What the API client fails with when it never sent the request: not signed in,
// or the session couldn't be read.
const NOT_SENT = ['NOT_SIGNED_IN', 'SESSION_UNAVAILABLE'];

// The API's answers to a registration it turned down before saving anything.
// Not PASSKEY_ALREADY_REGISTERED, which says the passkey is saved.
const REGISTRATION_REFUSALS = [
  PASSKEY_ERRORS.CHALLENGE_EXPIRED,
  PASSKEY_ERRORS.FORBIDDEN,
  PASSKEY_ERRORS.INVALID_REGISTRATION,
  PASSKEY_ERRORS.LIMIT_REACHED,
  PASSKEY_ERRORS.RECENT_SIGN_IN_REQUIRED,
  PASSKEY_ERRORS.SIGNED_IN_BEFORE_RESET,
  PASSKEY_ERRORS.UNAVAILABLE,
  PASSKEY_ERRORS.VERIFICATION_FAILED,
];

/**
 * Whether the API definitely didn't register a passkey: it refused it with one
 * of its own refusal codes or a 401 (the request never got past sign-in), or
 * the request was never sent. Any other 4xx (a gateway's 408 or 429, say), a
 * timeout, a lost connection or a 5xx can come after the server has saved it,
 * so none of them counts.
 */
export const registrationRefused = err =>
  err?.name === 'PasskeyApiError' &&
  (err.status === 401 ||
    REGISTRATION_REFUSALS.includes(err.code) ||
    NOT_SENT.includes(err.code));

/** A passkey timestamp (epoch ms) as a date, e.g. "3 Mar 2026". */
export const formatPasskeyDate = timestamp =>
  new Date(timestamp).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

/** When a passkey was added and last used, e.g. "Added 3 Mar 2026, last used 9 Mar 2026". */
export const describePasskeyUse = passkey =>
  [
    `Added ${formatPasskeyDate(passkey.CreatedAt)}`,
    passkey.LastUsedAt
      ? `last used ${formatPasskeyDate(passkey.LastUsedAt)}`
      : null,
  ]
    .filter(part => !!part)
    .join(', ');
